/**
 * Landing by itself (`autoLand`, §5.5, §5.16) — the clamp the application
 * settings, a workspace's override and an organization's default all go
 * through, and the line each surface prints for it. Its own module, free of
 * the agents' catalogue, so the workspace validator can read it.
 */
import { DEFAULT_SETTINGS } from './defaults';
import { bool, oneOf } from './primitives';
import type { AutoLand } from './types';

/**
 * Each automatic step only once the one before it is on: a push with no merge
 * before it, or a pull request with no push, would be a switch that does
 * nothing while saying it does.
 */
export function autoLand(input: unknown, fallback: AutoLand): AutoLand {
  const raw = (typeof input === 'object' && input !== null ? input : {}) as Partial<AutoLand>;
  const branch = bool(raw.branch) ?? fallback.branch;
  const message = branch && (bool(raw.message) ?? fallback.message);
  const push = message && (bool(raw.push) ?? fallback.push);
  const pullRequest = push && (bool(raw.pullRequest) ?? fallback.pullRequest);
  const session = (oneOf(raw.session, ['off', 'stop', 'close']) as AutoLand['session']) ?? fallback.session;
  return { branch, message, push, pullRequest, session: message ? session : 'off' };
}

/** A workspace's or an organization's answer: anything but an object is no answer, a missing step is off. */
export function autoLandOverride(input: unknown): AutoLand | null {
  return typeof input === 'object' && input !== null && !Array.isArray(input)
    ? autoLand(input, DEFAULT_SETTINGS.autoLand)
    : null;
}

/** `Branch · merge · push · pull request · then stopped`, or `Off` — the steps that run, in their order. */
export function autoLandSummary(land: AutoLand): string {
  if (!land.branch) return 'Off';
  const steps = ['Branch', land.message && 'merge', land.push && 'push', land.pullRequest && 'pull request'].filter(Boolean);
  const then = land.session === 'stop' ? 'then stopped' : land.session === 'close' ? 'then closed' : null;
  return [...steps, then].filter(Boolean).join(' · ');
}
